/**
 * Centralized date formatting utility
 * Formats dates according to the standard:
 * - With year: YYYY/MM/DD (e.g., 2025/01/15)
 * - Without year: MM/DD (e.g., 01/15)
 */

/**
 * Format date with year (YYYY/MM/DD)
 * @param date - Date string, Date object, or timestamp
 * @returns Formatted date string in YYYY/MM/DD format
 */
export function formatDateWithYear(date: string | Date | number | null | undefined): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
}

/**
 * Format date without year (MM/DD)
 * @param date - Date string, Date object, or timestamp
 * @returns Formatted date string in MM/DD format
 */
export function formatDateWithoutYear(date: string | Date | number | null | undefined): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${month}/${day}`;
}

/**
 * Format time (HH:MM)
 * @param date - Date string, Date object, or timestamp
 * @returns Formatted time string in HH:MM format
 */
export function formatTime(date: string | Date | number | null | undefined): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Format date with year (YYYY/MM/DD) and time (HH:MM)
 * @param date - Date string, Date object, or timestamp
 * @returns Formatted date and time string in YYYY/MM/DD HH:MM format
 */
export function formatDateTime(date: string | Date | number | null | undefined): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  return `${formatDateWithYear(dateObj)} ${formatTime(dateObj)}`;
}
